// A choice that grants consent may only take effect once the server has
// recorded it (recordFirst). A choice that only takes consent away takes
// effect at once; its record is kept locally and retried, here and on later
// page loads, until the server has it (recordLater).
const PENDING_KEY = 'cookie-consent:pending:v3';
const DECISIONS = ['accepted', 'custom', 'declined', 'embed'];

// A choice is what the consent endpoint takes: { decision, categories }, plus
// the provider for consent given inside a single embed.
function isChoice(choice) {
    return DECISIONS.includes(choice?.decision)
        && Array.isArray(choice.categories)
        && choice.categories.every((category) => typeof category === 'string')
        && (choice.provider === undefined || typeof choice.provider === 'string');
}

// No response at all (network), an expired CSRF token, throttling and server
// errors can succeed on a later attempt. Anything else (e.g. 422) won't.
function isRetryable(error) {
    const status = error?.response?.status;
    return status === undefined || [408, 419, 429].includes(status) || status >= 500;
}

function parsePending(raw) {
    try {
        const pending = JSON.parse(raw);
        return isChoice(pending?.choice) && typeof pending.token === 'string' ? pending : null;
    } catch {
        return null;
    }
}

export function createCookieConsentSubmission({
    storage,
    submit,
    refreshCsrf = async () => {},
    sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    retryDelays = [1000, 3000],
}) {
    // Storage can be missing or throw (private mode, blocked site data); the
    // in-memory copy still lets this page retry.
    let pending = null;
    try {
        pending = parsePending(storage?.getItem(PENDING_KEY) ?? null);
    } catch {}

    function setPending(value) {
        pending = value;
        try {
            value === null ? storage?.removeItem(PENDING_KEY) : storage?.setItem(PENDING_KEY, JSON.stringify(value));
        } catch {}
    }

    // Bumped by every new banner choice so retries for a superseded one stop
    // early. Consent given inside an embed supersedes nothing (current = null):
    // it neither stops nor is stopped by banner choices.
    let generation = 0;
    // One request at a time, so an unrecorded choice always lands before a later one.
    let queue = Promise.resolve();
    const enqueue = (job) => (queue = queue.then(job, job));

    async function send(choice, current) {
        for (let attempt = 0; ; attempt++) {
            try {
                await submit(choice);
                return true;
            } catch (error) {
                if (attempt >= retryDelays.length || !isRetryable(error)) return false;
                await sleep(retryDelays[attempt]);
                if (current !== null && current !== generation) return false;
                if (error?.response?.status === 419) {
                    await refreshCsrf().catch(() => {});
                }
            }
        }
    }

    async function flush(current) {
        const sending = pending;
        if (sending === null) return true;
        const stored = await send(sending.choice, current);
        // A newer choice made while this one was in flight stays pending for its own flush.
        if (stored && pending?.token === sending.token) setPending(null);
        return stored;
    }

    // Resolves true once the server has recorded the choice. The caller
    // applies it straight away and does not wait for this.
    function recordLater(choice) {
        const current = ++generation;
        setPending({ choice, token: `${Date.now()}-${Math.random()}` });
        return enqueue(() => flush(current));
    }

    // Resolves true only once the server has recorded the choice; the caller
    // must not apply it otherwise.
    function recordFirst(choice, { supersedesEarlier = true } = {}) {
        const current = supersedesEarlier ? ++generation : null;
        return enqueue(async () => {
            if (!(await flush(current))) return false;
            return send(choice, current);
        });
    }

    // Retries a choice left over from an earlier page load.
    function flushPending() {
        const current = generation;
        return enqueue(() => flush(current));
    }

    return { recordFirst, recordLater, flushPending, hasPending: () => pending !== null };
}
